// atlasChannel.ts — the seam the column renders from. S1 built the shell and
// the disconnected default; A1 (COCKPIT-ASSISTANT-SPEC.md, brief
// A1-CORE-BRIEF.md) adds `createCoreAtlasChannel` — the conversation over
// Core's /api/assistant routes — without changing the components that
// consume this interface.
//
// `AtlasChannel.items` is a discriminated union keyed on `kind`, not a fixed
// chat-message shape — A1 (or a later chunk) may add a receipt row
// ("Done — … · Undo") or a status card without this file's consumers ever
// throwing: render code must switch on `kind` and treat anything it does not
// recognise as nothing, never an error. Only `message` exists today.
import { isCockpitSurfaceKey, type CockpitSurfaceKey } from '@vowric/shared';


/** Fired on `window` when a to-do may have changed outside the screen showing it. */
export const TASKS_CHANGED_EVENT = 'vowric:tasks-changed';

export function announceTasksChanged(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(TASKS_CHANGED_EVENT));
}

export interface AtlasMessageItem {
  kind: 'message';
  id: string;
  from: 'atlas' | 'you';
  text: string;
  at: string; // ISO timestamp
}

/** A quiet one-liner — not a message from either side, e.g. "Reading your
 * calendar…". `tone` is advisory styling only (warn reads less muted);
 * absent means 'info'. */
export interface AtlasStatusItem {
  kind: 'status';
  id: string;
  text: string;
  at: string;
  tone?: 'info' | 'warn';
}

/** "Done — … · Undo" — an act atlas-cockpit took, with an optional reversal.
 * `undo.run()` is called synchronously by the column's own Undo button;
 * A1 (or whichever occupant wrote the receipt) owns what it actually does. */
export interface AtlasReceiptItem {
  kind: 'receipt';
  id: string;
  text: string;
  at: string;
  undo?: { label: string; run: () => void };
}

/** G2 — a question Core asks the person (“<title>”: mark it done instead, or delete?). It is the text plus
 * two buttons; once either is pressed both are disabled and the chosen one stays marked. `answer` is
 * absent on a fixture or once answered. Only the person's own tap ever reaches Core's answering routes. */
export interface AtlasAskItem {
  kind: 'ask';
  id: string;
  text: string;
  at: string;
  answered: 'done' | 'delete' | null;
  answer?: (choice: 'done' | 'delete') => void;
}

/** H4a (COCKPIT-HOME-COLUMNS-SPEC.md §H4) — one row of a builder pane's
 * OUTBOUND-ONLY journal (never `message`/`receipt`: nothing goes INTO the
 * pane from the web this chunk, and a builder row has no undo). `kind`
 * mirrors `builder_feed.kind` exactly minus `'status'` (a status row is a
 * BuilderFeedItem too at the wire level, but AtlasColumn.tsx's own render
 * pulls it into the header line instead of the scrolling feed — see its
 * `visibleItems` filter). */
export interface BuilderFeedItem {
  kind: 'action' | 'task_opened' | 'task_closed';
  id: string;
  text: string;
  at: string; // ISO timestamp — the journal's own time
}

// Consumers are written against `kind` defensively (a switch with a
// default-returns-nothing branch, GUARDED by an exhaustiveness check — see
// AtlasColumn.tsx's renderItem) specifically so a value a later chunk adds —
// before this union is updated to name it — still renders as nothing
// instead of crashing the column, while a newly NAMED kind this file forgot
// to handle fails typecheck instead of silently rendering nothing.
export type AtlasChannelItem = AtlasMessageItem | AtlasStatusItem | AtlasReceiptItem | AtlasAskItem | BuilderFeedItem;

/** Who is on the other end of an AtlasChannel. 'atlas' is the only one A1
 * wires up; `builder:${string}` names a future reviewed-builder occupant
 * CockpitFrame can mount in its place — S1 ships the type and the column's
 * heading/label swap only, no other behaviour. Declared here (not in
 * CockpitFrame.tsx) so AtlasColumn.tsx can import it without a cycle. */
export type CockpitOccupant = 'atlas' | `builder:${string}`;

/** What a sent message carries about where the person was. */
export interface AtlasSendContext {
  screen: string | null;
  recordIds: string[];
}

export interface AtlasChannel {
  /** Whether the cockpit has anywhere to send a message. S1 ships this
   * always false — there is no server endpoint yet (that is A1). Not
   * `readonly`: a test fixture may set it directly (see atlasChannel's own
   * default implementation and AtlasColumn.test.tsx), but production code
   * should treat these three fields as the implementation's, read through
   * this interface, and change them only via setDraft/send. */
  connected: boolean;
  items: AtlasChannelItem[];
  /** The composer's unsent text. Lifted into the channel (rather than kept
   * as component-local state) specifically so it is part of "the column's
   * state" the spec requires to survive a tab switch, provably, through
   * this same seam A1 will later drive. */
  draft: string;
  setDraft(text: string): void;
  send(text: string): void;
  /** Where the person is in the cockpit — carried on the next message they
   * send ({screen, recordIds}). Optional so a fixture channel need not care. */
  setContext?(context: AtlasSendContext): void;
  /** eb659c8d — "Atlas can switch screens on request": notified with a surface key when such a request
   * arrives LIVE (never from history). Returns the unsubscribe. Optional. */
  onNavigate?(listener: (surface: CockpitSurfaceKey) => void): () => void;
  /** Stops any background work (a long-poll, listeners). Optional. */
  dispose?(): void;
  /** Notified on any change to connected/items/draft. Returns the
   * unsubscribe function. */
  subscribe(listener: () => void): () => void;
}

/** The default implementation: not connected, nothing to show, nothing to
 * send to. A1 replaces this (or wraps it) once the Core endpoint exists;
 * nothing else in the column changes. */
class DisconnectedAtlasChannel implements AtlasChannel {
  connected = false;
  items: AtlasChannelItem[] = [];
  draft = '';

  private readonly listeners = new Set<() => void>();

  setDraft(text: string): void {
    this.draft = text;
    this.notify();
  }

  send(_text: string): void {
    // Not connected — there is nowhere to send this yet. A1 gives this a
    // real body; until then it is a deliberate no-op, never a throw, so a
    // stray call (e.g. a keyboard Enter on a disabled composer) is inert.
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

export function createAtlasChannel(): AtlasChannel {
  return new DisconnectedAtlasChannel();
}

/* ------------------------------------------------------------------ *
 * A1 — the conversation over Core
 * ------------------------------------------------------------------ */

const NIL_ID = '00000000-0000-0000-0000-000000000000';

/** A row as Core's /api/assistant/messages answers it. */
interface CoreRow {
  id: string;
  author: 'person' | 'atlas';
  kind: 'message' | 'status' | 'receipt' | 'ask';
  text: string;
  ask?: { kind: string; taskId: string; answered: 'done' | 'delete' | null } | null;
  tone: 'info' | 'warn' | null;
  undoes: string | null;
  navigate?: string | null;
  receipt: { id: string; verb: string; undoneAt: string | null } | null;
  createdAt: string;
}

/** A navigate row older than this is history, not a request — it never moves the screen (e.g. on a tab
 * coming back from hidden and catching up). */
const NAVIGATE_FRESH_MS = 60_000;

/** The slice of `document` the channel watches. */
export interface VisibilitySource {
  readonly visibilityState: string;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface CoreAtlasChannelOptions {
  tenantId: string;
  /** Injected by tests; the browser's fetch otherwise. */
  fetchFn?: typeof fetch;
  /** Injected by tests; the page's document otherwise. */
  doc?: VisibilitySource;
  /** How long one long-poll may hold, in seconds (Core caps it at 25). */
  waitSeconds?: number;
  /** Pause after a failed poll before the next, in ms. */
  retryMs?: number;
}

/**
 * The column's conversation over Core:
 *   - `connected` turns true when the first read succeeds (and stays false,
 *     with the column's honest sentence, when it does not);
 *   - `send` posts the text with the current {screen, recordIds};
 *   - a long-poll loop brings in new rows from either side, and STOPS while
 *     the tab is hidden (resuming, and catching up, when it is visible again);
 *   - a receipt carries its Undo, which calls the undo route; the "Undone — …"
 *     row that comes back takes the Undo off the receipt it reversed.
 * Nothing here names an occupant: the same channel serves whoever answers.
 */
class CoreAtlasChannel implements AtlasChannel {
  connected = false;
  items: AtlasChannelItem[] = [];
  draft = '';

  private readonly listeners = new Set<() => void>();
  private readonly navigateListeners = new Set<(surface: CockpitSurfaceKey) => void>();
  private readonly fetchFn: typeof fetch;
  private readonly doc: CoreAtlasChannelOptions['doc'] | null;
  private readonly waitSeconds: number;
  private readonly retryMs: number;
  private context: AtlasSendContext = { screen: null, recordIds: [] };
  private lastId: string | null = null;
  private running = false;
  private disposed = false;
  private inflight: AbortController | null = null;
  private readonly seen = new Set<string>();
  private readonly undone = new Set<string>();

  constructor(private readonly opts: CoreAtlasChannelOptions) {
    this.fetchFn = opts.fetchFn ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
    this.doc = opts.doc ?? (typeof document !== 'undefined' ? document : null);
    this.waitSeconds = opts.waitSeconds ?? 25;
    this.retryMs = opts.retryMs ?? 5000;
    this.doc?.addEventListener('visibilitychange', this.onVisibility);
    void this.start();
  }

  setDraft(text: string): void {
    this.draft = text;
    this.notify();
  }

  setContext(context: AtlasSendContext): void {
    this.context = { screen: context.screen, recordIds: [...context.recordIds] };
  }

  send(text: string): void {
    const trimmed = text.trim();
    if (!this.connected || !trimmed) return;
    const context = this.context;
    this.draft = '';
    this.notify();
    void this.request<{ message: CoreRow }>('POST', '/api/assistant/messages', { text: trimmed, context })
      .then((r) => this.absorb([r.message], false))
      .catch((err: unknown) => {
        // Never silent: the text comes back to the composer, and the reason is said.
        if (!this.draft) this.draft = trimmed;
        this.pushLocal(`Not sent — ${errorSentence(err)}`, 'warn');
      });
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  onNavigate(listener: (surface: CockpitSurfaceKey) => void): () => void {
    this.navigateListeners.add(listener);
    return () => {
      this.navigateListeners.delete(listener);
    };
  }

  dispose(): void {
    this.disposed = true;
    this.running = false;
    this.inflight?.abort();
    this.doc?.removeEventListener('visibilitychange', this.onVisibility);
  }

  /** Test seam: whether the long-poll loop is running right now. */
  get polling(): boolean {
    return this.running;
  }

  private readonly onVisibility = (): void => {
    if (this.hidden()) {
      this.running = false;
      this.inflight?.abort();
    } else {
      void this.start();
    }
  };

  private hidden(): boolean {
    return this.doc?.visibilityState === 'hidden';
  }

  private async start(): Promise<void> {
    if (this.disposed || this.running || this.hidden()) return;
    this.running = true;
    if (!this.connected) {
      try {
        const r = await this.request<{ messages: CoreRow[] }>('GET', '/api/assistant/messages');
        this.connected = true;
        this.absorb(r.messages, false); // history: never moves the screen
      } catch {
        this.running = false;
        this.notify();
        return;
      }
    }
    void this.loop();
  }

  private async loop(): Promise<void> {
    while (this.running && !this.disposed && !this.hidden()) {
      const ac = new AbortController();
      this.inflight = ac;
      try {
        // An empty conversation still long-polls: every id sorts after the nil id,
        // so the server holds the request until the first row exists (a bare read
        // without `after` answers at once, and would spin).
        const after = encodeURIComponent(this.lastId ?? NIL_ID);
        const r = await this.request<{ messages: CoreRow[] }>('GET', `/api/assistant/messages?after=${after}&wait=${this.waitSeconds}`, undefined, ac.signal);
        this.absorb(r.messages, true);
      } catch {
        if (ac.signal.aborted) break;
        await new Promise((resolve) => setTimeout(resolve, this.retryMs));
      } finally {
        if (this.inflight === ac) this.inflight = null;
      }
    }
    this.running = false;
  }

  private absorb(rows: CoreRow[], live: boolean): void {
    let changed = false;
    for (const row of rows) {
      if (!this.lastId || row.id > this.lastId) this.lastId = row.id;
      if (this.seen.has(row.id)) continue;
      this.seen.add(row.id);
      // eb659c8d: Atlas switches the screen — ONLY for a row that just arrived live, ONLY to a key of the
      // fixed list (Core refused anything else; this is the second lock), never from history.
      if (
        live &&
        row.author === 'atlas' &&
        row.kind === 'status' &&
        isCockpitSurfaceKey(row.navigate) &&
        Date.now() - Date.parse(row.createdAt) < NAVIGATE_FRESH_MS
      ) {
        const surface = row.navigate;
        for (const l of this.navigateListeners) l(surface);
      }
      // A to-do Atlas just made, changed or reversed: tell any open to-do list to read again (Huan 2026-10-17:
      // "it didn't auto refresh the screen"). Live rows only; an Undo's verb is not on its row, so any Undo counts.
      if (live && row.author === 'atlas' && ((row.kind === 'receipt' && row.receipt?.verb.startsWith('task')) || row.undoes)) {
        announceTasksChanged();
      }
      if (row.undoes) {
        this.undone.add(row.undoes);
        this.items = this.items.map((it) => (it.kind === 'receipt' && it.id === `receipt:${row.undoes}` ? { ...it, undo: undefined } : it));
      }
      const item = this.toItem(row);
      if (item) {
        this.items = [...this.items, item];
        changed = true;
      }
    }
    if (changed || rows.length) this.notify();
  }

  private toItem(row: CoreRow): AtlasChannelItem | null {
    switch (row.kind) {
      case 'message':
        return { kind: 'message', id: row.id, from: row.author === 'person' ? 'you' : 'atlas', text: row.text, at: row.createdAt };
      case 'status':
        return { kind: 'status', id: row.id, text: row.text, at: row.createdAt, ...(row.tone ? { tone: row.tone } : {}) };
      case 'receipt': {
        const receiptId = row.receipt?.id;
        const undoable = receiptId && !row.receipt?.undoneAt && !this.undone.has(receiptId);
        // The visible text is the receipt's own sentence; Undo is the button beside it.
        return {
          kind: 'receipt',
          id: receiptId ? `receipt:${receiptId}` : row.id,
          text: row.text,
          at: row.createdAt,
          ...(undoable ? { undo: { label: 'Undo', run: () => this.undo(receiptId) } } : {}),
        };
      }
      case 'ask': {
        const answered = row.ask?.answered ?? null;
        return {
          kind: 'ask',
          id: row.id,
          text: row.text,
          at: row.createdAt,
          answered,
          ...(answered ? {} : { answer: (choice: 'done' | 'delete') => this.answerAsk(row.id, choice) }),
        };
      }
      default:
        return null;
    }
  }

  /** The person's tap on one of an ask's two buttons. Off at once (a double tap is one request); back on if it fails. */
  private answerAsk(askId: string, choice: 'done' | 'delete'): void {
    const before = this.items.find((it) => it.kind === 'ask' && it.id === askId);
    this.items = this.items.map((it) => (it === before ? { ...(it as AtlasAskItem), answered: choice, answer: undefined } : it));
    this.notify();
    void this.request<{ message: CoreRow }>('POST', `/api/assistant/asks/${encodeURIComponent(askId)}/${choice}`)
      .then((r) => this.absorb([r.message], false))
      .catch((err: unknown) => {
        const status = err instanceof ChannelError ? err.status : 0;
        // 409 = already answered (another tab, or the to-do changed): it stays settled. Anything else: tap again.
        if (status !== 409 && before) this.items = this.items.map((it) => (it.id === askId ? before : it));
        this.pushLocal(`Not changed — ${errorSentence(err)}`, 'warn');
      });
  }

  private undo(receiptId: string): void {
    // Off at once, so a double tap is one request; back on if the undo fails.
    const restore = this.items.find((it) => it.kind === 'receipt' && it.id === `receipt:${receiptId}`);
    this.items = this.items.map((it) => (it === restore ? { ...it, undo: undefined } : it));
    this.notify();
    void this.request<{ message: CoreRow }>('POST', `/api/assistant/receipts/${encodeURIComponent(receiptId)}/undo`)
      .then((r) => this.absorb([r.message], false))
      .catch((err: unknown) => {
        const status = err instanceof ChannelError ? err.status : 0;
        if (status !== 409 && restore) this.items = this.items.map((it) => (it.id === restore.id ? restore : it));
        this.pushLocal(`Not undone — ${errorSentence(err)}`, 'warn');
      });
  }

  private pushLocal(text: string, tone: 'info' | 'warn'): void {
    this.items = [...this.items, { kind: 'status', id: `local:${Date.now()}:${this.items.length}`, text, at: new Date().toISOString(), tone }];
    this.notify();
  }

  private async request<T>(method: 'GET' | 'POST', path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    const headers = new Headers({ 'X-Tenant-Id': this.opts.tenantId });
    if (body !== undefined) headers.set('Content-Type', 'application/json');
    const resp = await this.fetchFn(path, {
      method,
      headers,
      credentials: 'same-origin',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
    const parsed = (await resp.json().catch(() => null)) as { error?: { message?: string } } | null;
    if (!resp.ok) throw new ChannelError(resp.status, parsed?.error?.message ?? null);
    return parsed as T;
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

class ChannelError extends Error {
  constructor(
    readonly status: number,
    readonly serverSentence: string | null
  ) {
    super(serverSentence ?? `the server answered ${status}`);
    this.name = 'ChannelError';
  }
}

/** The server's own sentence when it gave one; otherwise what happened, plainly. */
function errorSentence(err: unknown): string {
  if (err instanceof ChannelError) return err.serverSentence ?? `the server answered ${err.status}`;
  return 'the cockpit could not reach the server';
}

export function createCoreAtlasChannel(opts: CoreAtlasChannelOptions): AtlasChannel {
  return new CoreAtlasChannel(opts);
}
